// lab/src/component/HomePage/CategorySection/AllPackages.js

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import { 
  Search, Filter, Heart, Star, Clock, ArrowRight, 
  ShoppingBag, Package as PackageIcon, Droplet, 
  Activity, Wind, Shield, TrendingUp,
  ChevronDown, ChevronUp, X, Loader2,
  GitCompare, Check, Minus, Plus,
  ChevronLeft, ChevronRight, MapPin, Flame
} from 'lucide-react';
import GetPackageDetails from '../../LabTest/GetPackageDetails';
import ReplaceCartPopup from '../../LabTest/ReplaceCartPopup';
import { usePackageCartActions } from '../../../hooks/usePackageCartActions';
import { useCartActions } from '../../../hooks/useCartActions';
import { 
  fetchWishlist, 
  addToWishlist, 
  removeFromWishlist,
  addWishlistOptimistic,
  removeWishlistOptimistic,
} from '../../../Redux/wishlistSlice';
import { fetchAllRatings } from '../../../Redux/ratingSlice';
import LoginPopup from '../../common/LoginPopup';
import { useLocationContext } from '../../../context/LocationContext';

import {
  fetchAllPackagesFromAPI,
  getPackageImageUrl,
  getUserId,
  getTestsCount,
  filterAndSortPackages
} from '../../../services/HomePage/AllPackagesAPI';

export default function AllPackages({ labId }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { coords, selectedCity, address } = useLocationContext();
  
  const cart = useSelector((state) => state.cart.items || []);
  const ratings = useSelector((state) => state.rating.ratings || {});
  const ratingLoading = useSelector((state) => state.rating.loading);
  const wishlistItems = useSelector((state) => state.wishlist.items || []);
  const wishlistLoading = useSelector((state) => state.wishlist.loading);
  
  const [packages, setPackages] = useState([]);
  const [filteredPackages, setFilteredPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLab, setSelectedLab] = useState('all');
  const [selectedCompany, setSelectedCompany] = useState('all');
  const [sortBy, setSortBy] = useState('popular');
  const [showFilters, setShowFilters] = useState(false);
  const [priceRange, setPriceRange] = useState({ min: 0, max: 20000 });
  
  // ✅ Location filter state
  const [selectedLocation, setSelectedLocation] = useState('all');
  const [availableLocations, setAvailableLocations] = useState([]);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 30;
  
  const [compareList, setCompareList] = useState([]);
  const [showCompareModal, setShowCompareModal] = useState(false);
  const MAX_COMPARE = 4;
  const [selectedPackageId, setSelectedPackageId] = useState(null);
  const [showPackageDetails, setShowPackageDetails] = useState(false);

  // Local state for login popup
  const [showLoginPopup, setShowLoginPopup] = useState(false);

  const { addPackage, removePackage, hasDifferentStore } = usePackageCartActions();
  
  const { 
    addWithReplaceCheck, 
    removeItem, 
    LoginPopupComponent
  } = useCartActions();

  // ================= DISTANCE CALCULATION =================
  const getDistanceKm = (lat1, lon1, lat2, lon2) => {
    if (lat1 == null || lon1 == null || lat2 == null || lon2 == null) return 999;
    const R = 6371;
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLon = ((lon2 - lon1) * Math.PI) / 180;
    const a = Math.sin(dLat / 2) ** 2 +
              Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
              Math.sin(dLon / 2) ** 2;
    return 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)) * R;
  };

  // ✅ Get package coordinates (handles multiple field names)
  const getPackageLat = (pkg) => {
    return Number(pkg.latitude || pkg.Latitude || pkg.lat || pkg.Lat || null);
  };

  const getPackageLng = (pkg) => {
    return Number(pkg.longitude || pkg.Longitude || pkg.Longnitude || pkg.lng || pkg.Lng || null);
  };

  const hasValidCoords = (pkg) => {
    const lat = getPackageLat(pkg);
    const lng = getPackageLng(pkg);
    return !isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0;
  };

  // ================= WISHLIST FUNCTIONS =================
  const loadWishlist = async () => {
    const userId = getUserId();
    if (!userId) return;
    try {
      await dispatch(fetchWishlist(userId)).unwrap();
    } catch (error) {
      console.error('Error loading wishlist:', error);
    }
  };

  const isInWishlist = (productId, categoryId = 3) => {
    return wishlistItems.some(
      item => Number(item.productId) === Number(productId) && 
              Number(item.categoryId) === Number(categoryId)
    );
  };

  const toggleWishlist = async (pkg, e) => {
    e.stopPropagation();
    const userId = getUserId();
    
    if (!userId) {
      setShowLoginPopup(true);
      return;
    }

    const productId = pkg.packageId;
    const categoryId = 3;
    const isWished = isInWishlist(productId, categoryId);

    if (isWished) {
      dispatch(removeWishlistOptimistic({ productId, categoryId }));
    } else {
      dispatch(addWishlistOptimistic({ productId, categoryId }));
    }

    try {
      if (isWished) {
        await dispatch(removeFromWishlist({ userId, productId, categoryId })).unwrap();
      } else {
        await dispatch(addToWishlist({ userId, productId, categoryId })).unwrap();
      }
    } catch (error) {
      console.error('Error toggling wishlist:', error);
      if (isWished) {
        dispatch(addWishlistOptimistic({ productId, categoryId }));
      } else {
        dispatch(removeWishlistOptimistic({ productId, categoryId }));
      }
      alert('Failed to update wishlist. Please try again.');
    }
  };

  // ================= HELPER FUNCTIONS =================
  const getPackageIcon = (name) => {
    if (!name) return <PackageIcon size={24} />;
    const nameLower = name.toLowerCase();
    if (nameLower.includes('diabetes')) return <Droplet size={24} />;
    if (nameLower.includes('heart') || nameLower.includes('cardiac')) return <Activity size={24} />;
    if (nameLower.includes('thyroid')) return <Wind size={24} />;
    if (nameLower.includes('lft') || nameLower.includes('liver')) return <PackageIcon size={24} />;
    if (nameLower.includes('full') || nameLower.includes('body')) return <Shield size={24} />;
    return <PackageIcon size={24} />;
  };

  const handlePackageClick = (pkg) => {
    setSelectedPackageId(pkg.packageId);
    setShowPackageDetails(true);
  };

  const isPackageAdded = (packageId) => {
    return cart.some(
      (item) =>
        Number(item.productId) === Number(packageId) &&
        item.categoryId === 3
    );
  };

  const handlePackageAction = (pkg, e) => {
    e.stopPropagation();
    
    const userId = getUserId();
    
    if (!userId) {
      setShowLoginPopup(true);
      return;
    }
    
    const payload = {
      productId: pkg.packageId,
      categoryId: 3,
      price: Number(pkg.packagePrice) || 0,
      storeId: Number(pkg.labId) || 0,
      productName: pkg.packageName || "",
    };
    
    const added = isPackageAdded(pkg.packageId);
    if (added) {
      removeItem(payload);
    } else {
      addWithReplaceCheck(payload);
    }
  };

  const addToCompare = (pkg) => {
    if (compareList.some(item => item.packageId === pkg.packageId)) {
      setCompareList(compareList.filter(item => item.packageId !== pkg.packageId));
      return;
    }
    
    if (compareList.length >= MAX_COMPARE) {
      alert(`You can compare maximum ${MAX_COMPARE} packages at a time.`);
      return;
    }
    
    setCompareList([...compareList, pkg]);
  };

  const removeFromCompare = (packageId) => {
    setCompareList(compareList.filter(item => item.packageId !== packageId));
  };

  const clearCompare = () => {
    setCompareList([]);
  };

  const isInCompare = (packageId) => {
    return compareList.some(item => item.packageId === packageId);
  };

  const getPackageRating = (packageId) => {
    const key = `${packageId}_3`;
    return ratings[key] || { average: 0, total: 0, ratings: [] };
  };

  // ✅ Get unique locations from packages
  const getUniqueLocations = (packages) => {
    const locationSet = new Set();
    packages.forEach(pkg => {
      const loc = pkg.location || pkg.city || pkg.area || pkg.Location || pkg.City;
      if (loc) locationSet.add(loc);
    });
    return Array.from(locationSet).map(loc => ({ id: loc, name: loc }));
  };

  // ✅ Filter packages by location
  const filterByLocation = (packages, location) => {
    if (location === 'all') return packages;
    return packages.filter(pkg => {
      const pkgLocation = pkg.location || pkg.city || pkg.area || pkg.Location || pkg.City || '';
      return pkgLocation.toLowerCase().includes(location.toLowerCase());
    });
  };

  // ✅ Get unique labs
  const getUniqueLabs = (packages) => {
    const labMap = new Map();
    packages.forEach(pkg => {
      if (pkg.labId && pkg.labName) {
        labMap.set(pkg.labId, { id: pkg.labId, name: pkg.labName });
      }
    });
    return Array.from(labMap.values());
  };

  // ✅ Get unique companies
  const getUniqueCompanies = (packages) => {
    const companyMap = new Map();
    packages.forEach(pkg => {
      if (pkg.companyId && pkg.companyName) {
        companyMap.set(pkg.companyId, { id: pkg.companyId, name: pkg.companyName });
      }
    });
    return Array.from(companyMap.values());
  };

  // ================= FETCH PACKAGES =================
  const fetchAllPackages = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('📌 Fetching packages with labId:', labId);
      
      // ✅ Fetch packages and labs in parallel
      const [allPackagesRaw, labsRes] = await Promise.all([
        fetchAllPackagesFromAPI(labId),
        axios.get('https://admin.medrphalabs.com/api/Lab').catch(() => ({ data: [] }))
      ]);

      const labsData = Array.isArray(labsRes.data) ? labsRes.data : (Array.isArray(labsRes.data?.data) ? labsRes.data.data : []);
      const allPackages = Array.isArray(allPackagesRaw) ? allPackagesRaw : [];
      
      console.log('📌 Total packages fetched:', allPackages.length);
      
      // ✅ Filter active packages and map parent lab coordinates & names
      const activePackages = allPackages
        .filter(pkg => pkg.isActive === 1 || pkg.isActive === true)
        .map(pkg => {
          const matchedLab = labsData.find(l => Number(l.id) === Number(pkg.labId));
          const lat = Number(pkg.latitude || pkg.Latitude || pkg.lat || pkg.Lat || matchedLab?.latitude || matchedLab?.Latitude || matchedLab?.lat || null);
          const lng = Number(pkg.longitude || pkg.Longitude || pkg.Longnitude || pkg.longnitude || pkg.lng || pkg.Lng || matchedLab?.longnitude || matchedLab?.longitude || matchedLab?.lng || null);
          const labName = pkg.labName || matchedLab?.name || matchedLab?.labName || matchedLab?.lab_name || (pkg.labId === 1 ? 'DGchem Lab' : (pkg.labId === 3 ? 'DOCOPD LAB' : 'Certified Partner Lab'));
          const companyName = pkg.companyName || matchedLab?.companyName || matchedLab?.company_name || 'Partner Diagnostics';

          return {
            ...pkg,
            latitude: lat,
            longitude: lng,
            labName: labName,
            companyName: companyName,
          };
        });
      
      console.log('📌 Active packages:', activePackages.length);
      
      setPackages(activePackages);
      
      // ✅ Extract unique locations
      const locations = getUniqueLocations(activePackages);
      setAvailableLocations(locations);
      
      // ✅ Apply location filter (20km radius) - same as AllTests.js
      let filtered = activePackages;
      
      const sourceLat = selectedCity?.latitude ?? selectedCity?.lat ?? coords?.lat;
      const sourceLng = selectedCity?.longitude ?? selectedCity?.lng ?? coords?.lng;

      if (sourceLat && sourceLng) {
        console.log('📍 Source location:', { sourceLat, sourceLng });
        
        filtered = activePackages.filter(pkg => {
          const pkgLat = getPackageLat(pkg);
          const pkgLng = getPackageLng(pkg);
          
          // ✅ Only show packages within 20km radius
          if (!hasValidCoords(pkg)) {
            return false;
          }
          
          const distance = getDistanceKm(Number(sourceLat), Number(sourceLng), pkgLat, pkgLng);
          
          if (distance > 20) {
            return false;
          }
          
          return true;
        });
        
        console.log('📌 After location filter (20km):', filtered.length);
      }
      
      // ✅ Auto-select location if available
      if (coords && selectedCity && !labId) {
        const userCity = selectedCity.name || selectedCity.city || selectedCity.location || '';
        if (userCity) {
          const locationMatch = locations.find(loc => 
            loc.name.toLowerCase().includes(userCity.toLowerCase()) ||
            userCity.toLowerCase().includes(loc.name.toLowerCase())
          );
          if (locationMatch) {
            setSelectedLocation(locationMatch.id);
            filtered = filterByLocation(filtered, locationMatch.id);
          }
        }
      }
      
      setFilteredPackages(filtered);
      setCurrentPage(1);
      
      if (activePackages.length > 0) {
        const ratingProducts = activePackages.map(pkg => ({
          productId: pkg.packageId,
          categoryId: 3
        }));
        await dispatch(fetchAllRatings(ratingProducts));
      }
      
      await loadWishlist();
      
    } catch (err) {
      console.error('API Error:', err);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllPackages();
  }, [labId, coords, selectedCity]);

  // ✅ Apply filters including location
  useEffect(() => {
    let filtered = packages;
    
    // ✅ Location filter - 20km radius (exactly like AllTests.js)
    const sourceLat = selectedCity?.latitude ?? selectedCity?.lat ?? coords?.lat;
    const sourceLng = selectedCity?.longitude ?? selectedCity?.lng ?? coords?.lng;

    if (sourceLat && sourceLng) {
      filtered = filtered.filter(pkg => {
        const pkgLat = getPackageLat(pkg);
        const pkgLng = getPackageLng(pkg);
        
        if (!hasValidCoords(pkg)) return false;
        
        const distance = getDistanceKm(Number(sourceLat), Number(sourceLng), pkgLat, pkgLng);
        
        if (distance > 20) return false;
        
        return true;
      });
    }
    
    // Apply other filters
    const filters = { searchTerm, selectedLab, selectedCompany, sortBy, priceRange };
    filtered = filterAndSortPackages(filtered, filters);
    
    setFilteredPackages(filtered);
    setCurrentPage(1);
  }, [packages, searchTerm, selectedLab, selectedCompany, sortBy, priceRange, coords, selectedCity]);

  // ================= PAGINATION =================
  const totalPages = Math.ceil(filteredPackages.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentPackages = filteredPackages.slice(startIndex, endIndex);

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const goToPage = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxPagesToShow = 5;
    
    if (totalPages <= maxPagesToShow) {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) {
          pageNumbers.push(i);
        }
        pageNumbers.push('...');
        pageNumbers.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pageNumbers.push(1);
        pageNumbers.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) {
          pageNumbers.push(i);
        }
      } else {
        pageNumbers.push(1);
        pageNumbers.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pageNumbers.push(i);
        }
        pageNumbers.push('...');
        pageNumbers.push(totalPages);
      }
    }
    
    return pageNumbers;
  };

  const uniqueLabs = getUniqueLabs(packages);
  const uniqueCompanies = getUniqueCompanies(packages);

  const renderStars = (rating, size = 12) => {
    const stars = [];
    const roundedRating = Math.round(rating || 0);
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <Star
          key={i}
          size={size}
          className={`${
            i <= roundedRating
              ? 'text-yellow-500 fill-yellow-500'
              : 'text-gray-300'
          }`}
        />
      );
    }
    return stars;
  };

  // ================= COMPARE MODAL =================
  const CompareModal = () => {
    if (!showCompareModal || compareList.length < 2) return null;

    const getAllTestNames = () => {
      const testSet = new Set();
      compareList.forEach(pkg => {
        if (pkg.testPackageDetailsJson) {
          try {
            const tests = JSON.parse(pkg.testPackageDetailsJson);
            if (Array.isArray(tests)) {
              tests.forEach(test => {
                if (test.testName) testSet.add(test.testName);
              });
            }
          } catch {}
        }
      });
      return Array.from(testSet);
    };

    const allTestNames = getAllTestNames();

    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-7xl w-full max-h-[90vh] overflow-hidden">
          <div className="sticky top-0 bg-gradient-to-r from-red-50 to-red-100 p-4 border-b flex items-center justify-between">
            <div className="flex items-center gap-3">
              <GitCompare size={24} className="text-red-600" />
              <h2 className="text-xl font-bold text-gray-800">
                Compare Packages ({compareList.length}/{MAX_COMPARE})
              </h2>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={clearCompare}
                className="text-sm text-red-600 hover:text-red-800 font-medium px-3 py-1 rounded-lg hover:bg-red-50 transition-colors"
              >
                Clear All
              </button>
              <button
                onClick={() => setShowCompareModal(false)}
                className="p-2 hover:bg-red-200 rounded-lg transition-colors"
              >
                <X size={20} />
              </button>
            </div>
          </div>

          <div className="overflow-auto p-4 max-h-[calc(90vh-80px)]">
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="sticky left-0 bg-gray-50 p-3 text-left text-sm font-semibold text-gray-700 border-b min-w-[120px]">
                      Features
                    </th>
                    {compareList.map(pkg => (
                      <th key={pkg.packageId} className="p-3 text-center border-b min-w-[200px]">
                        <div className="flex flex-col items-center">
                          <div className="p-2 bg-red-50 rounded-lg mb-2">
                            {getPackageIcon(pkg.packageName)}
                          </div>
                          <h3 className="text-sm font-bold text-gray-800 line-clamp-2">
                            {pkg.packageName}
                          </h3>
                          <button
                            onClick={() => removeFromCompare(pkg.packageId)}
                            className="mt-1 text-xs text-red-500 hover:text-red-700"
                          >
                            Remove
                          </button>
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr className="hover:bg-gray-50">
                    <td className="sticky left-0 bg-white p-3 text-sm font-semibold text-gray-700 border-b">
                      💰 Price
                    </td>
                    {compareList.map(pkg => {
                      const finalPrice = Number(pkg.packagePrice) || 0;
                      const originalPrice = Number(pkg.totalPrice) || 0;
                      const discountAmount = Number(pkg.discountPrice) || 0;
                      const hasDiscount = discountAmount > 0 && originalPrice > 0 && finalPrice < originalPrice;
                      return (
                        <td key={pkg.packageId} className="p-3 text-center border-b">
                          <span className="text-lg font-bold text-green-600">
                            ₹{finalPrice.toLocaleString()}
                          </span>
                          {hasDiscount && (
                            <span className="text-xs text-gray-400 line-through ml-2">
                              ₹{originalPrice.toLocaleString()}
                            </span>
                          )}
                          {hasDiscount && (
                            <div className="text-[10px] text-red-500 font-semibold">
                              Save ₹{discountAmount.toLocaleString()}
                            </div>
                          )}
                        </td>
                      );
                    })}
                  </tr>

                  <tr className="hover:bg-gray-50">
                    <td className="sticky left-0 bg-white p-3 text-sm font-semibold text-gray-700 border-b">
                      🎯 Discount
                    </td>
                    {compareList.map(pkg => {
                      const finalPrice = Number(pkg.packagePrice) || 0;
                      const originalPrice = Number(pkg.totalPrice) || 0;
                      const discountAmount = Number(pkg.discountPrice) || 0;
                      const hasDiscount = discountAmount > 0 && originalPrice > 0 && finalPrice < originalPrice;
                      const discountPercent = originalPrice > 0 && discountAmount > 0 
                        ? Math.round((discountAmount / originalPrice) * 100) 
                        : 0;
                      return (
                        <td key={pkg.packageId} className="p-3 text-center border-b">
                          {hasDiscount ? (
                            <span className="inline-block bg-green-100 text-green-700 px-2 py-1 rounded-full text-sm font-bold">
                              {discountPercent}% OFF
                            </span>
                          ) : (
                            <span className="text-gray-400">No Discount</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>

                  <tr className="hover:bg-gray-50">
                    <td className="sticky left-0 bg-white p-3 text-sm font-semibold text-gray-700 border-b">
                      📋 Total Tests
                    </td>
                    {compareList.map(pkg => {
                      const count = getTestsCount(pkg);
                      return (
                        <td key={pkg.packageId} className="p-3 text-center border-b">
                          <span className="text-lg font-bold text-blue-600">{count}</span>
                          <span className="text-sm text-gray-500"> tests</span>
                        </td>
                      );
                    })}
                  </tr>

                  <tr className="hover:bg-gray-50">
                    <td className="sticky left-0 bg-white p-3 text-sm font-semibold text-gray-700 border-b">
                      🏥 Lab
                    </td>
                    {compareList.map(pkg => (
                      <td key={pkg.packageId} className="p-3 text-center border-b">
                        <span className="text-sm">{pkg.labName || 'N/A'}</span>
                      </td>
                    ))}
                  </tr>

                  <tr>
                    <td colSpan={compareList.length + 1} className="p-4 bg-gray-50">
                      <div className="flex justify-center gap-3">
                        <button
                          onClick={() => setShowCompareModal(false)}
                          className="px-6 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-medium transition-colors"
                        >
                          Close
                        </button>
                        <button
                          onClick={() => {
                            setShowCompareModal(false);
                            if (compareList.length > 0) {
                              setSelectedPackageId(compareList[0].packageId);
                              setShowPackageDetails(true);
                            }
                          }}
                          className="px-6 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg font-medium transition-colors"
                        >
                          View Details
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ================= LOCATION LOADING STATE =================
  if (!coords?.lat || !coords?.lng) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-10 w-10 border-4 border-red-500 border-t-transparent"></div>
          <p className="mt-4 text-gray-600">📍 Detecting your location...</p>
        </div>
      </div>
    );
  }

  // ================= LOADING STATE =================
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 size={48} className="animate-spin text-red-500 mx-auto" />
          <p className="mt-4 text-gray-600">Loading packages...</p>
        </div>
      </div>
    );
  }

  // ================= ERROR STATE =================
  if (error) {
    return (
      <div className="px-4 md:px-8 py-6 bg-white rounded-xl shadow-lg mt-3 text-center">
        <div className="inline-flex items-center gap-2 bg-gradient-to-r from-red-500 to-red-600 text-white px-4 py-1.5 rounded-full shadow-md mb-4">
          <span className="text-xs font-bold tracking-wide">⚠️ ERROR</span>
        </div>
        <div className="flex flex-col items-center justify-center py-8">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h3 className="text-xl font-bold text-gray-800 mb-2">Oops! Something went wrong</h3>
          <p className="text-gray-600 text-sm">{error}</p>
          <button
            onClick={() => fetchAllPackages()}
            className="mt-4 px-6 py-2 bg-red-500 text-white rounded-full text-sm font-medium hover:bg-red-600 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // ================= EMPTY STATE =================
  if (!loading && packages.length === 0) {
    return (
      <div className="px-4 md:px-8 py-6 bg-white rounded-xl shadow-lg mt-3 text-center">
        <div className="inline-flex items-center gap-2 bg-gradient-to-r from-red-500 to-red-600 text-white px-4 py-1.5 rounded-full shadow-md mb-4">
          <ShoppingBag size={12} className="text-yellow-400" />
          <span className="text-xs font-bold tracking-wide">PACKAGES</span>
          </div>
        <div className="flex flex-col items-center justify-center py-8">
          <PackageIcon size={48} className="text-gray-300 mb-4" />
          <h3 className="text-xl font-bold text-gray-700 mb-2">
            {labId ? 'No Packages Available for this Lab' : 'No Packages Available'}
          </h3>
          <p className="text-gray-500 text-sm max-w-md">
            {labId 
              ? 'This lab currently has no active packages listed within 20km radius.' 
              : 'No active packages found within 20km radius. Please check back later.'}
          </p>
          {labId && (
            <p className="text-xs text-gray-400 mt-2">
              Lab ID: {labId}
            </p>
          )}
          <button
            onClick={() => {
              if (labId) {
                navigate('/all-packages');
              } else {
                fetchAllPackages();
              }
            }}
            className="mt-4 px-6 py-2 bg-red-500 text-white rounded-full text-sm font-medium hover:bg-red-600 transition-colors"
          >
            {labId ? 'View All Packages' : 'Retry'}
          </button>
        </div>
      </div>
    );
  }

  // ================= MAIN RENDER =================
  return (
    <>
    {/* ✅ Full-width page container matching LabList */}
    <div className={`w-full min-h-screen ${labId ? 'bg-white py-4 px-2' : 'bg-[#F8FAFC] pb-24'} text-slate-800 font-sans`}>
      
      {showPackageDetails && (
        <GetPackageDetails
          id={selectedPackageId}
          onClose={() => setShowPackageDetails(false)}
        />
      )}

      <CompareModal />

      {/* ✅ Full Width Header Banner for /all-packages */}
      {!labId && (
        <div className="bg-gradient-to-r from-red-600 via-rose-600 to-red-700 text-white py-6 px-4 sm:px-6 lg:px-10 shadow-md">
          <div className="w-full max-w-[1720px] mx-auto">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <div className="flex items-center gap-2">
                  <span className="bg-white/20 text-white text-[10px] font-black px-2.5 py-0.5 rounded-full uppercase tracking-wider flex items-center gap-1">
                    <Flame size={11} className="text-amber-300" />
                    <span>Certified Health Packages</span>
                  </span>
                  <span className="text-red-100 text-xs font-semibold">
                    {filteredPackages.length} Packages Available
                  </span>
                </div>
                <h1 className="text-2xl sm:text-3xl font-black mt-1 tracking-tight">
                  Comprehensive Health Packages
                </h1>
                <p className="text-xs sm:text-sm text-red-100 mt-1 flex items-center gap-1.5 font-medium">
                  <MapPin size={14} className="text-yellow-300 shrink-0" />
                  <span className="truncate max-w-lg">{selectedCity?.name || selectedCity?.cityName || address || 'Showing packages near you within 20km radius'}</span>
                </p>
              </div>

              <div className="flex items-center gap-3 flex-wrap">
                {selectedCity && (
                  <span className="text-xs text-white bg-white/20 backdrop-blur-xs px-3.5 py-1.5 rounded-full border border-white/30 font-bold">
                    📍 {selectedCity.name || selectedCity.cityName || selectedCity.city || 'Nearby'} (20km)
                  </span>
                )}
                {compareList.length > 0 && (
                  <button
                    onClick={() => setShowCompareModal(true)}
                    className="flex items-center gap-2 bg-white text-red-600 px-4 py-2 rounded-xl text-xs font-black hover:bg-red-50 transition-colors shadow-lg"
                  >
                    <GitCompare size={14} />
                    <span>Compare ({compareList.length})</span>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content Area */}
      <div className={`w-full ${labId ? 'max-w-full' : 'max-w-[1720px] px-4 sm:px-6 lg:px-10 mt-6'} mx-auto`}>

        {/* ✅ Agar labId hai toh simple heading */}
        {labId && (
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-xl font-bold text-gray-800">
                📦 Available Packages
              </h2>
              <p className="text-xs text-gray-500">
                {filteredPackages.length} packages found within 20km radius
              </p>
              {labId && (
                <p className="text-xs text-gray-400">
                  Lab ID: {labId}
                </p>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => navigate('/all-packages')}
                className="text-xs text-red-500 hover:text-red-600 font-medium px-3 py-1.5 rounded-full border border-red-500 hover:bg-red-50 transition-colors"
              >
                View All
              </button>
              {compareList.length > 0 && (
                <button
                  onClick={() => setShowCompareModal(true)}
                  className="flex items-center gap-2 bg-red-500 text-white px-3 py-1.5 rounded-full text-xs font-bold hover:bg-red-600 transition-colors shadow-md"
                >
                  <GitCompare size={14} />
                  Compare ({compareList.length})
                </button>
              )}
            </div>
          </div>
        )}

        {/* ✅ Search & Filters - sirf tab dikhao jab labId nahi hai */}
        {!labId && (
          <div className="bg-white rounded-xl shadow-md p-4 mb-6">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                <input
                  type="text"
                  placeholder="Search packages by name, lab or description..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X size={18} />
                  </button>
                )}
              </div>

              {/* ✅ Location Filter Dropdown */}
              {availableLocations.length > 0 && !labId && (
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                  <select
                    value={selectedLocation}
                    onChange={(e) => setSelectedLocation(e.target.value)}
                    className="pl-9 pr-8 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 bg-white appearance-none min-w-[140px]"
                  >
                    <option value="all">📍 All Locations</option>
                    {availableLocations.map(loc => (
                      <option key={loc.id} value={loc.id}>{loc.name}</option>
                    ))}
                  </select>
                  <ChevronDown size={16} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" />
                </div>
              )}

              <button
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center gap-2 px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                <Filter size={18} />
                <span>Filters</span>
                {showFilters ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </button>

              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 bg-white"
              >
                <option value="popular">Popular</option>
                <option value="price-low">Price: Low to High</option>
                <option value="price-high">Price: High to Low</option>
                <option value="discount">Biggest Discount</option>
                <option value="name">Name A-Z</option>
              </select>
            </div>

            {showFilters && (
              <div className="mt-4 pt-4 border-t border-gray-200 grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Lab</label>
                  <select
                    value={selectedLab}
                    onChange={(e) => setSelectedLab(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                  >
                    <option value="all">All Labs</option>
                    {uniqueLabs.map(lab => (
                      <option key={lab.id} value={lab.id}>{lab.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Company</label>
                  <select
                    value={selectedCompany}
                    onChange={(e) => setSelectedCompany(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                  >
                    <option value="all">All Companies</option>
                    {uniqueCompanies.map(company => (
                      <option key={company.id} value={company.id}>{company.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Max Price: ₹{priceRange.max}</label>
                  <input
                    type="range"
                    min="0"
                    max="20000"
                    step="50"
                    value={priceRange.max}
                    onChange={(e) => setPriceRange({ ...priceRange, max: parseInt(e.target.value) })}
                    className="w-full accent-red-500"
                  />
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>₹0</span>
                    <span>₹20,000</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* No results */}
        {filteredPackages.length === 0 && (
          <div className="text-center py-12 bg-white rounded-xl shadow-lg">
            <ShoppingBag size={48} className="mx-auto text-gray-300 mb-4" />
            <h3 className="text-xl font-bold text-gray-700">No packages found</h3>
            <p className="text-gray-500 mt-2">
              {coords?.lat && coords?.lng 
                ? 'No packages available within 20km radius. Try adjusting your location or filters.'
                : 'Try adjusting your search or filters'}
            </p>
            <button
              onClick={() => {
                setSearchTerm('');
                setSelectedLab('all');
                setSelectedCompany('all');
                setSelectedLocation('all');
                setPriceRange({ min: 0, max: 20000 });
              }}
              className="mt-4 text-red-500 hover:text-red-600 text-sm font-semibold"
            >
              Clear all filters
            </button>
          </div>
        )}

        {/* ✅ PACKAGES GRID - Matching AllTests gap & spacing exactly */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {currentPackages.map((pkg) => {
            // ✅ CORRECT PRICE CALCULATION
            const finalPrice = Number(pkg.packagePrice) || 0;
            const originalPrice = Number(pkg.totalPrice) || 0;
            const discountAmount = Number(pkg.discountPrice) || 0;
            
            const hasDiscount = discountAmount > 0 && originalPrice > 0 && finalPrice < originalPrice;
            const discountPercent = originalPrice > 0 && discountAmount > 0 
              ? Math.round((discountAmount / originalPrice) * 100) 
              : 0;
            
            const { average: averageRating, total: totalReviews } = getPackageRating(pkg.packageId);
            
            const inCompare = isInCompare(pkg.packageId);
            const inCart = isPackageAdded(pkg.packageId);
            const inWishlist = isInWishlist(pkg.packageId, 3);
            const hasPopular = pkg.isPopular === true;
            const hasFasting = pkg.isFasting === true;
            
            // ✅ Calculate distance for this package
            let distance = null;
            if (coords?.lat && coords?.lng && hasValidCoords(pkg)) {
              const pkgLat = getPackageLat(pkg);
              const pkgLng = getPackageLng(pkg);
              const sourceLat = selectedCity?.latitude ?? selectedCity?.lat ?? coords?.lat;
              const sourceLng = selectedCity?.longitude ?? selectedCity?.lng ?? coords?.lng;
              if (sourceLat && sourceLng) {
                distance = getDistanceKm(Number(sourceLat), Number(sourceLng), pkgLat, pkgLng);
              }
            }
            
            return (
              <div
                key={pkg.packageId}
                className="bg-white border border-slate-200/90 hover:border-red-400 rounded-2xl p-3.5 flex flex-col justify-between hover:shadow-lg transition-all duration-300 relative group overflow-hidden"
              >
                {/* Top Image Box or Name Fallback */}
                <div className="h-36 sm:h-40 w-full rounded-xl bg-slate-50 border border-slate-100 flex items-center justify-center p-2.5 relative overflow-hidden mb-3">
                  {/* Discount Badge */}
                  {hasDiscount && discountAmount > 0 && (
                    <div className="absolute top-2 left-2 flex items-center gap-1 bg-[#E53935] text-white text-[9px] font-extrabold px-1.5 py-0.5 rounded shadow-2xs z-10">
                      <span>{discountPercent}% OFF</span>
                    </div>
                  )}

                  {/* Wishlist Button on Top Right */}
                  <button
                    onClick={(e) => toggleWishlist(pkg, e)}
                    className="absolute top-2 right-2 p-1.5 rounded-full bg-white/90 backdrop-blur-xs shadow-xs hover:bg-white hover:scale-110 transition-all duration-200 z-10 border border-slate-100"
                    disabled={wishlistLoading}
                    title="Wishlist"
                  >
                    {wishlistLoading ? (
                      <div className="w-3.5 h-3.5 border-2 border-red-500 border-t-transparent rounded-full animate-spin"></div>
                    ) : (
                      <Heart 
                        size={12} 
                        className={`transition-colors duration-200 ${
                          inWishlist 
                            ? 'fill-red-500 text-red-500' 
                            : 'text-gray-400 hover:text-red-500'
                        }`}
                      />
                    )}
                  </button>

                  {/* Test count badge */}
                  <div className="absolute bottom-2 left-2 flex items-center gap-1 bg-white/95 backdrop-blur-xs text-red-700 text-[9px] font-extrabold px-1.5 py-0.5 rounded shadow-xs border border-red-100 z-10">
                    <span>📦 {getTestsCount(pkg)} Tests</span>
                  </div>

                  {/* Dynamic Image or Fallback */}
                  {(() => {
                    const imgUrl = getPackageImageUrl(pkg);

                    return (
                      <>
                        {imgUrl ? (
                          <img
                            src={imgUrl}
                            alt={pkg.packageName || 'Package'}
                            className="max-h-full max-w-full object-contain group-hover:scale-105 transition-transform duration-300"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              const fallback = e.target.nextElementSibling;
                              if (fallback) fallback.classList.remove('hidden');
                            }}
                          />
                        ) : null}

                        {/* Styled Package Name Fallback Box */}
                        <div 
                          className={`name-fallback w-full h-full flex flex-col items-center justify-center text-center p-2.5 bg-gradient-to-br from-red-50 to-rose-100/60 rounded-lg ${imgUrl ? 'hidden' : 'flex'}`}
                        >
                          <div className="w-10 h-10 rounded-full bg-[#E53935] text-white flex items-center justify-center mb-1.5 shadow-md">
                            <PackageIcon size={18} />
                          </div>
                          <span className="text-[11px] font-black text-slate-800 line-clamp-2 leading-tight text-center">
                            {pkg.packageName}
                          </span>
                        </div>
                      </>
                    );
                  })()}
                </div>

                <div>
                  {/* Title & Lab */}
                  <div className="mb-2">
                    <div className="text-[10px] text-green-700 font-semibold flex items-center gap-1 mb-1">
                      <span>✓ {pkg.labName || 'Verified Lab'}</span>
                    </div>
                    <h3 
                      onClick={() => {
                        setSelectedPackageId(pkg.packageId);
                        setShowPackageDetails(true);
                      }}
                      className="text-xs sm:text-sm font-extrabold text-gray-900 line-clamp-2 group-hover:text-red-600 transition-colors cursor-pointer leading-snug"
                    >
                      {pkg.packageName}
                    </h3>
                  </div>

                  <p className="text-xs text-gray-500 mb-2 line-clamp-2 min-h-[32px] leading-relaxed">
                    {pkg.description || 'Comprehensive health package with pathologist verified digital reports.'}
                  </p>

                  {/* Rating & Test count pill */}
                  <div className="flex items-center justify-between text-xs mb-3 pt-1 border-t border-gray-50">
                    <div className="flex items-center gap-1">
                      <span className="inline-flex items-center gap-0.5 bg-green-700 text-white text-[10px] font-bold px-1.5 py-0.2 rounded">
                        <span>{averageRating ? averageRating.toFixed(1) : "4.8"}</span>
                        <Star size={7} fill="white" />
                      </span>
                      <span className="text-[10px] text-gray-400">({totalReviews || "1.5k"})</span>
                    </div>
                    <span className="text-[10px] font-bold text-red-700 bg-red-50 px-2 py-0.5 rounded-md border border-red-100">
                      {getTestsCount(pkg)} Tests
                    </span>
                  </div>
                </div>

                {/* Price & Action Button */}
                <div className="pt-2.5 border-t border-gray-100 mt-auto">
                  <div className="flex items-baseline justify-between mb-2">
                    <div className="flex items-baseline gap-1.5">
                      <span className="text-base font-black text-gray-900">
                        ₹{finalPrice.toLocaleString()}
                      </span>
                      {originalPrice > 0 && originalPrice > finalPrice && (
                        <span className="text-[11px] line-through text-gray-400">
                          ₹{originalPrice.toLocaleString()}
                        </span>
                      )}
                    </div>
                    {discountAmount > 0 && (
                      <span className="text-[10px] font-bold text-green-600">
                        Save ₹{discountAmount.toLocaleString()}
                      </span>
                    )}
                  </div>

                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handlePackageAction(pkg, e);
                    }}
                    className={`w-full py-2 rounded-xl text-xs font-bold transition-all shadow-xs flex items-center justify-center gap-1.5 ${
                      inCart
                        ? 'bg-green-50 text-green-700 border border-green-200 hover:bg-green-100'
                        : 'bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white'
                    }`}
                  >
                    <ShoppingBag size={13} />
                    <span>{inCart ? 'In Cart • Remove' : 'Book Package'}</span>
                  </button>
                </div>

                {/* COMPARE BUTTON */}
                <div className="mt-2 pt-2 border-t border-gray-100 flex items-center justify-between">
                  <span className="text-[9px] text-gray-400 font-medium">
                    🔄 {getTestsCount(pkg)} tests
                  </span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      addToCompare(pkg);
                    }}
                    className={`flex items-center gap-1 px-2 py-0.5 rounded-md text-[9px] font-medium transition-all duration-200 ${
                      inCompare 
                        ? 'bg-green-100 text-green-700 border border-green-300 hover:bg-green-200' 
                        : 'bg-purple-100 text-purple-700 border border-purple-300 hover:bg-purple-200'
                    }`}
                  >
                    <GitCompare size={11} />
                    {inCompare ? 'Added' : 'Compare'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* PAGINATION */}
        {!loading && totalPages > 1 && (
          <div className="flex justify-center items-center gap-2 mt-8 mb-4 flex-wrap">
            <button
              onClick={goToPreviousPage}
              disabled={currentPage === 1}
              className={`px-3 py-2 rounded-md flex items-center gap-1 transition-colors duration-200 text-sm
                ${
                  currentPage === 1
                    ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                    : "bg-blue-500 text-white hover:bg-blue-600"
                }`}
            >
              <ChevronLeft size={14} />
              Previous
            </button>

            <div className="flex gap-1 flex-wrap">
              {getPageNumbers().map((page, index) => (
                <button
                  key={index}
                  onClick={() => typeof page === 'number' && goToPage(page)}
                  className={`px-3 py-2 rounded-md transition-colors duration-200 min-w-[40px] text-sm
                    ${
                      currentPage === page
                        ? "bg-red-600 text-white"
                        : page === '...'
                        ? "bg-gray-100 text-gray-600 cursor-default"
                        : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                    }`}
                  disabled={page === '...'}
                >
                  {page}
                </button>
              ))}
            </div>

            <button
              onClick={goToNextPage}
              disabled={currentPage === totalPages}
              className={`px-3 py-2 rounded-md flex items-center gap-1 transition-colors duration-200 text-sm
                ${
                  currentPage === totalPages
                    ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                    : "bg-blue-500 text-white hover:bg-blue-600"
                }`}
            >
              Next
              <ChevronRight size={14} />
            </button>
          </div>
        )}

        {/* Showing info */}
        {!loading && filteredPackages.length > 0 && (
          <div className="text-center text-sm text-gray-500 mt-2">
            Showing {startIndex + 1} - {Math.min(endIndex, filteredPackages.length)} of {filteredPackages.length} packages
            {selectedCity && (
              <span className="ml-2 text-green-600 font-medium">
                📍 within 20km
              </span>
            )}
          </div>
        )}

        {/* Stats - sirf tab dikhao jab labId nahi hai */}
        {!labId && (
          <div className="mt-8 pt-6 border-t border-gray-200">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="flex items-center justify-center gap-2 text-sm text-gray-600">
                <Shield size={18} className="text-green-500" />
                <span>{packages.length} Active Packages</span>
              </div>
              <div className="flex items-center justify-center gap-2 text-sm text-gray-600">
                <Star size={18} className="text-yellow-500" />
                <span>{packages.filter(p => p.isPopular).length} Popular Packages</span>
              </div>
              <div className="flex items-center justify-center gap-2 text-sm text-gray-600">
                <Activity size={18} className="text-blue-500" />
                <span>{uniqueLabs.length} Partner Labs</span>
              </div>
              <div className="flex items-center justify-center gap-2 text-sm text-gray-600">
                <Heart size={18} className="text-red-500" />
                <span>{uniqueCompanies.length} Trusted Companies</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
    
    {/* Login Popup */}
    <LoginPopup
      isOpen={showLoginPopup}
      onClose={() => setShowLoginPopup(false)}
    />
    
    {LoginPopupComponent}
    </>
  );
}